import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import NonAffiliationBanner from "../components/NonAffiliationBanner";
import EventCard from "../components/EventCard";
import { LoadingState, ErrorState, EmptyState } from "../components/StateBlocks";
import useEvents from "../hooks/useEvents";

export default function Home() {
  const { events, loading, error } = useEvents("upcoming");
  const nextConfirmed = events.find((e) => e.status === "CONFIRMED" || e.status === "FULL");
  const upcoming = events.slice(0, 3);

  return (
    <>
      <Seo
        title="Home"
        description="HS Library Hangout organizes parent-managed board game gatherings for homeschooled high schoolers in the southwest suburbs of Chicago."
      />
      <NonAffiliationBanner />

      <section className="card">
        <h1>Board games, teens, and a room full of friends</h1>
        <p>
          HS Library Hangout organizes informal board game gatherings for homeschooled high school teens
          (grades 8-12, ages 13-18) in the southwest suburbs of Chicago, IL. Parents RSVP for their own teens, stay
          present for the gathering, and everyone has a great time playing games together at Oak Lawn
          Public Library.
        </p>
        <Link className="btn btn-primary" to="/events">
          See upcoming events
        </Link>
      </section>

      <section aria-labelledby="next-event-heading" className="card">
        <h2 id="next-event-heading">Next confirmed event</h2>
        {loading && <LoadingState label="Checking for the next event\u2026" />}
        {error && <ErrorState message={error} />}
        {!loading && !error && nextConfirmed && <EventCard event={nextConfirmed} />}
        {!loading && !error && !nextConfirmed && (
          <EmptyState title="No confirmed events yet">
            <p>Check back soon, or view all planned/upcoming events.</p>
          </EmptyState>
        )}
      </section>

      <section aria-labelledby="upcoming-heading" className="card">
        <h2 id="upcoming-heading">Upcoming events</h2>
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {!loading && !error && upcoming.length > 0 && (
          <div className="card-grid">
            {upcoming.map((event) => (
              <EventCard key={event.eventId} event={event} />
            ))}
          </div>
        )}
        {!loading && !error && upcoming.length === 0 && (
          <EmptyState title="No upcoming events yet">
            <p>We&rsquo;ll post new gatherings here as soon as they&rsquo;re planned.</p>
          </EmptyState>
        )}
      </section>

      <section aria-labelledby="how-it-works-heading" className="card">
        <h2 id="how-it-works-heading">How it works</h2>
        <ol>
          <li>We announce a planned gathering once we&rsquo;re working on reserving a library room.</li>
          <li>Once the room is confirmed, RSVPs open for parents to reserve spots for their teens.</li>
          <li>Parents receive an email to confirm their RSVP, then a link to manage it any time.</li>
          <li>An adult parent organizer is present for the entire gathering.</li>
        </ol>
        <Link to="/how-it-works">Read the full how-it-works guide</Link>
      </section>

      <section aria-labelledby="safety-heading" className="card">
        <h2 id="safety-heading">Safety first</h2>
        <p>
          A parent organizer is present for every gathering. We ask all families to review our safety and
          code of conduct expectations before attending.
        </p>
        <Link to="/safety">Read our Safety &amp; Code of Conduct</Link>
      </section>
    </>
  );
}
